import {
  useQueries,
  useQuery,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query"
import type { CustomChartSerie } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import type { MetricConfiguration } from "@tokenterminal/tt-analytics-api-types/dist/api/metrics"
import { metricIncludesSelf } from "@tokenterminal/tt-analytics-api-types/dist/api/metrics-constants"
import type { Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { convertApiToZoom } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom-constants"
import { formatVenueLabel } from "@tokenterminal/tt-analytics-api-types/dist/utils/formatVenueLabel"
import {
  calculateGranularityUnit,
  computePeriodDenominators,
  detectPartialPeriods,
  filterPartialPeriods,
  type GranularityType,
  groupDataBasedOnGranularity,
} from "@tokenterminal/tt-analytics-api-types/dist/utils/granularity"
import { useMemo } from "react"
import {
  aggregateBeyondLimitDynamic,
  type Payload,
} from "../../../utils/limitSeries"
import { toDictionary } from "../../../utils/toDictionary"
import { trpc, trpcClient } from "../../../utils/trpc"
import { getYaxisMergePaths } from "../store/chart/yaxis-options-atom"
import type { SERIE_FORMAT } from "../tt-ui-chart/ChartOptions"
import type {
  ChartSerie,
  ChartSerieTimeData,
} from "../tt-ui-chart/useHighchartOptions"
import { getPercentChangeSeries } from "../tt-ui-chart/utils/getPercentChangeSeries"
import { getSeriesColor } from "../utils/colors"
import { convertChartTypeToHighcharts, getStack } from "../utils/serie-type"
import { parseVenue } from "../utils/venue"

function getCurrentGroupCount(series: ChartSerie[]) {
  return series.filter(
    (serie) => (serie.info?.legendLabel ?? serie.info?.name) !== "Other"
  ).length
}

function getMetricApproximation(
  metric: Pick<MetricConfiguration, "static"> | undefined
): "average" | "sum" {
  if (metric?.static) {
    return "average"
  }
  return "sum"
}

// `detectPartialPeriods`, `shouldPreserveEarliestPeriod`, and
// `filterPartialPeriods` used to live here as module-private logic; they are
// now part of the canonical shared granularity module
// (`@tokenterminal/tt-analytics-api-types/dist/utils/granularity`) so the
// API's composed chart endpoint uses the exact same implementation.
// `getStack`/`convertChartTypeToHighcharts` similarly moved to
// `../utils/serie-type` so the composed-endpoint client hook shares them.

type TimeseriesItem = {
  value: number | null
  timestamp: string
  data_id?: string | null | undefined
  chain_id?: string | null | undefined
  product_id?: string | null | undefined
  market_sector?: string | null | undefined
  market_sector_id?: string | null | undefined
  asset_id?: string | null | undefined
  reference_asset?: string | null | undefined
  venue_data_id?: string | null | undefined
  venue_product_id?: string | null | undefined
}

type SortedDataIds = {
  data_id?: string | undefined
  chain_id?: string | undefined
  product_id?: string | undefined
  venue_data_id?: string | undefined
  venue_product_id?: string | undefined
  market_sector_id?: string | undefined
  asset_id?: string | undefined
  reference_asset?: string | undefined
}[]

export const getGroupByName = (
  groupBy:
    | "chain"
    | "project+chain"
    | "market-sector"
    | "reference-asset"
    | "project"
    | "product"
    | "product+chain"
    | "chain-project"
    | "project-product"
    | "product-market-sector"
    | "assets"
    | "chain-asset"
    | "venue"
    | null
) => {
  if (groupBy === "chain-project") {
    return "project+chain"
  }

  if (groupBy === "project-product") {
    return "product"
  }

  return groupBy === "project" ? "projects" : groupBy || "none"
}

export const getInnerKey = (
  groupBy: CustomChartSerie["groupBy"],
  key: string,
  data: SortedDataIds[0]
) => {
  let innerKey = ""
  switch (groupBy) {
    case "project+chain":
    case "chain-project":
      innerKey = `${key}:${data.chain_id}-${data.data_id}`

      break
    case "chain":
      if (data.market_sector_id) {
        innerKey = `${key}:${data.market_sector_id}`
      } else if (data.chain_id && data.data_id) {
        innerKey = `${key}:${data.data_id}-${data.chain_id}`
      } else if (data.chain_id) {
        innerKey = `${key}:${data.chain_id}`
      } else {
        innerKey = `${key}:${data.data_id}`
      }
      break
    case "project":
      innerKey = `${key}:${data.data_id}`

      break
    case "market-sector":
      innerKey = `${key}:${data.market_sector_id}`
      break
    case "reference-asset":
      innerKey = `${key}:${data.reference_asset}`
      break
    case "product":
      innerKey = `${key}:${data.product_id}`
      break
    case "product+chain":
      innerKey = `${key}:${data.product_id}-${data.chain_id}`
      break
    case "chain-market-sector":
      innerKey = `${key}:${data.chain_id}-${data.market_sector_id}`
      break
    case "product-market-sector":
      innerKey = `${key}:${data.market_sector_id}`
      break
    case "assets":
      innerKey = `${key}:${data.asset_id}`
      break
    case "chain-asset":
      innerKey = `${key}:${data.chain_id}-${data.asset_id}`
      break
    case "venue":
      // Delimit with ":" (not "-"): project and product ids routinely
      // contain hyphens, so ("morpho","blue-eth") and ("morpho-blue","eth")
      // would collide on a hyphen delimiter. ":" cannot appear in either id.
      innerKey = `${key}:${data.venue_data_id}:${data.venue_product_id}`
      break
    case "asset+venue":
      innerKey = `${key}:${data.asset_id}:${data.venue_data_id}:${data.venue_product_id}`
      break
    case "venue+chain":
      innerKey = `${key}:${data.venue_data_id}:${data.venue_product_id}:${data.chain_id}`
      break
    case "market-sector+venue":
      innerKey = `${key}:${data.market_sector_id}:${data.venue_data_id}:${data.venue_product_id}`
      break
    default:
      innerKey = `${key}:aggregated`
      break
  }

  return innerKey
}

type FormatLabelProps = {
  groupBy: CustomChartSerie["groupBy"]
  info: {
    data_id?: string | undefined
    name?: string | undefined
    chain_id?: string | undefined
    market_sector_id?: string | undefined
    reference_asset?: string | undefined
    product_id?: string | null
    asset_id?: string | undefined
    venue_data_id?: string | undefined
    venue_product_id?: string | undefined
  }
  project?: {
    name: string
  }
  /** Full data_id → project map, needed for `venue` group-by: the venue's
   * project (e.g. "Aave") is a different project than `project` above
   * (which is resolved from `info.data_id`, i.e. the asset's own issuer). */
  projects?: Map<string, { name: string }>
  chains?: Map<
    string,
    {
      symbol: string
      name: string
      logo: string
      chain_id: string
      product_id: string | null
      project_data_id: string
      project_slug: string | null
    }
  >
  marketSectors?: Map<
    string,
    {
      name: string
    }
  >
  products?: Map<
    string,
    {
      product_name: string
      data_id: string
      symbol?: string
    }
  >
  assets?: Map<
    string,
    {
      name: string
      asset_id: string
      symbol?: string
    }
  >
  productAssets?: Map<string, string>
  showProductSymbol?: boolean
  includeProductsInProjectBreakdown?: boolean
  /** Suppress the " (Project)" suffix on `product` group-by labels. Useful when
   * the breakdown is already scoped to a single project, so the suffix would be
   * redundant (e.g. "Aave v3 (Aave)" → "Aave v3"). Opt-in; off by default. */
  hideProductProjectSuffix?: boolean
}

const formatLabel = ({
  groupBy,
  info,
  project,
  projects,
  chains,
  marketSectors,
  products,
  assets,
  productAssets,
  showProductSymbol,
  includeProductsInProjectBreakdown,
  hideProductProjectSuffix,
}: FormatLabelProps) => {
  const projectName = project?.name ? project?.name : null
  const issuerSymbol = productAssets?.get(info.product_id ?? "")
    ? assets?.get(productAssets.get(info.product_id ?? "") ?? "")?.symbol
    : null
  // product_id is only unique per data_id (e.g. both `hyperliquid` and `tradexyz`
  // have a `perps` product), so look products up by the data_id+product_id composite.
  const productKey = info.product_id
    ? `${info.data_id ?? ""}:${info.product_id}`
    : null
  const productName = productKey
    ? showProductSymbol
      ? products?.get(productKey)?.symbol
      : issuerSymbol
        ? issuerSymbol
        : products?.get(productKey)?.product_name
    : null
  const assetSymbol = info.asset_id ? assets?.get(info.asset_id)?.symbol : null
  const chainName = info.chain_id ? chains?.get(info.chain_id)?.name : null
  const marketSectorName = info.market_sector_id
    ? marketSectors?.get(info.market_sector_id)?.name
    : null
  // Venue = the (project, product) that earned the metric (e.g. which lending
  // market or DEX a token is deposited in) — resolved the same way as
  // `product`, just keyed off venue_data_id/venue_product_id instead of
  // data_id/product_id since those describe the asset's own issuer, not
  // the venue.
  const venueProjectName = info.venue_data_id
    ? projects?.get(info.venue_data_id)?.name
    : null
  const venueProductKey = info.venue_product_id
    ? `${info.venue_data_id ?? ""}:${info.venue_product_id}`
    : null
  // Venue products are protocol/pool identifiers (e.g. Uniswap's univ2/
  // univ3/univ4), not token-like assets -- they don't have a meaningful
  // `symbol`, so always use `product_name` here regardless of
  // `showProductSymbol` (which is meant for the `product` groupBy, where
  // products often ARE tokens with real symbols).
  const venueProductNameLookup = venueProductKey
    ? products?.get(venueProductKey)?.product_name
    : null
  const venueProductName = venueProductNameLookup || info.venue_product_id || ""

  const venueLabel = formatVenueLabel(
    venueProjectName,
    venueProductNameLookup,
    venueProductName || info.venue_data_id || ""
  )

  // The overflow bucket stamps "other" on every identity field, so scan them
  // all (skip the display-only `name`) to catch it for every group-by.
  if (
    Object.entries(info).some(
      ([key, value]) => key !== "name" && value === "other"
    )
  ) {
    return "Other"
  }

  switch (groupBy) {
    case "chain":
      if (info.chain_id) {
        return `${chainName}`
      } else if (info.market_sector_id) {
        return `${marketSectorName}`
      } else {
        return `${projectName}`
      }

    case "project+chain":
    case "chain-project":
      return `${projectName} - (${chainName})`

    case "market-sector":
      return `${marketSectorName}`

    case "reference-asset":
      return `${info.reference_asset ?? ""}`

    case "product":
      if (productName) {
        return `${productName}${
          projectName && !hideProductProjectSuffix ? ` (${projectName})` : ""
        }`
      } else {
        return `${projectName ? `${projectName} - No Product Specified` : "No Product Specified"}`
      }
    case "product+chain":
      return `${productName} - (${chainName})`
    case "chain-market-sector":
      return `${
        marketSectors?.get(info.market_sector_id ?? "")?.name
      } (${chains?.get(info.chain_id ?? "")?.name})`
    case "product-market-sector":
      return `${marketSectorName}`
    case "assets":
      return `${assetSymbol}`
    case "chain-asset":
      return `${chains?.get(info.chain_id ?? "")?.name} - ${assetSymbol}`
    case "venue":
      return venueLabel
    case "asset+venue":
      return `${assetSymbol}${venueLabel ? ` (${venueLabel})` : ""}`
    case "venue+chain":
      return `${venueLabel}${chainName ? ` (${chainName})` : ""}`
    case "market-sector+venue":
      return `${marketSectorName}${venueLabel ? ` (${venueLabel})` : ""}`
    default:
      if (includeProductsInProjectBreakdown && info.product_id) {
        return `${productName}`
      }
      return `${projectName ?? info.name ?? info.data_id ?? ""}`
  }
}

export function useSeries({
  chartSerieSettings,
  zoom,
  granularity,
  showCurrentPeriod,
  includeAllChains = false,
  start,
  end,
  suspense = true,
  showProductSymbol = false,
  includeProductsInProjectBreakdown = false,
  hideProductProjectSuffix = false,
}: {
  chartSerieSettings: Array<CustomChartSerie>
  zoom: Interval
  granularity: GranularityType
  showCurrentPeriod?: boolean
  includeAllChains?: boolean
  start?: string
  end?: string
  /** By default, the hook will suspend, so you can just use `<Suspense>`
   * boundaries to manage the loading state. But if you set this to false, you
   * can use the `isLoading` property to handle the loading state instead. */
  suspense?: boolean
  showProductSymbol?: boolean
  includeProductsInProjectBreakdown?: boolean
  /** Drop the redundant " (Project)" suffix on product-grouped labels when the
   * breakdown is already scoped to one project. Opt-in; off by default. */
  hideProductProjectSuffix?: boolean
}) {
  const useMaybeSuspenseQuery = suspense ? useSuspenseQuery : useQuery
  const useMaybeSuspenseQueries = suspense ? useSuspenseQueries : useQueries

  // Reference lists below use the canonical tRPC `queryOptions` cache keys —
  // matching the page-side hooks (`useProjects`, `useChains`, `useAssetsList`,
  // `useMarketSectors`, `useMetricsConfigurationList`) — so every chart and
  // page hook shares ONE cached copy
  const { data: projects, isLoading: isProjectsLoading } =
    useMaybeSuspenseQuery(
      trpc.projects.getProjects.queryOptions(
        { include_metrics: false, include_placeholders: false },
        { select: (res) => res.data }
      )
    )

  const {
    data: projectMarketSectors,
    isLoading: isProjectMarketSectorsLoading,
  } = useMaybeSuspenseQuery(
    trpc.marketSectors.getMarketSectors.queryOptions(
      { type: "project" },
      { select: (res) => res.data }
    )
  )
  const {
    data: productMarketSectors,
    isLoading: isProductMarketSectorsLoading,
  } = useMaybeSuspenseQuery(
    trpc.marketSectors.getMarketSectors.queryOptions(
      { type: "product" },
      { select: (res) => res.data }
    )
  )

  const { data: assets = [], isLoading: isAssetsLoading } =
    useMaybeSuspenseQuery(
      trpc.asset.getAssets.queryOptions({}, { select: (res) => res.data })
    )

  // Create a lookup to pass a product_id and get back the issuer_id (i.e., asset_id)
  const productToIssuerDictionary = useMemo(() => {
    const map = new Map<string, string>()
    for (const asset of assets ?? []) {
      for (const product of asset.products ?? []) {
        if (
          product.relation_to_the_project === "issuer" &&
          product.product_id
        ) {
          map.set(product.product_id, asset.asset_id)
        }
      }
    }
    return map
  }, [assets])

  const marketSectors = useMemo(() => {
    return [...(projectMarketSectors ?? []), ...(productMarketSectors ?? [])]
  }, [projectMarketSectors, productMarketSectors])

  // `getChains` defaults `include_all_chains` to false server-side, so passing
  // no input is data-identical to the previous `{ include_all_chains: false }`
  // call AND matches `useChains()`'s (no-input) canonical key for the public
  // case. The all-chains case keeps its own distinct key.
  const { data: chains, isLoading: isChainsLoading } = useMaybeSuspenseQuery(
    trpc.chains.getChains.queryOptions(
      includeAllChains ? { include_all_chains: true } : undefined,
      { select: (res) => res.data }
    )
  )

  const { data: products = [], isLoading: isProductsLoading } =
    useMaybeSuspenseQuery(
      trpc.products.getProducts.queryOptions({}, { select: (res) => res.data })
    )

  const {
    data: metricsConfiguration,
    isLoading: isMetricsConfigurationLoading,
  } = useMaybeSuspenseQuery(
    trpc.metrics.getMetricsConfiguration.queryOptions(undefined, {
      select: (res) => res.data,
    })
  )

  // Daily percent-change series ask the server to close the interval
  // at its start, so the baseline point sits exactly `zoom` days
  // before the latest one — like the breakdown cards (DEV-5160).
  // getPercentChangeSeries needs no window logic for this: it already
  // pins the first eligible point at 0% and computes the rest against
  // it — the interval-start day simply becomes that point. Day
  // granularity only: week/month grouping would fold the extra day
  // into the first bucket. The server ignores the flag for
  // calendar-anchored zooms and explicit date ranges.
  // The trigger is chart-wide, not per serie: when any serie plots as a %
  // change line, the value series fetch the same extra day too. The %
  // line necessarily starts one day before a plain `zoom`-point window,
  // so without this the value series would start a day later — and the
  // shown % change could not be reproduced from a value serie's first
  // and last plotted points.
  const chartHasDailyPercentChangeSerie =
    granularity === "day" &&
    chartSerieSettings.some(
      (settings) =>
        settings?.filters?.length > 0 &&
        settings.display === "percentage" &&
        getStack(settings.chart_type, true) === undefined
    )

  const results: Array<{
    data: {
      sorted_data_ids: SortedDataIds
      data: TimeseriesItem[]
      total_sorted_data_ids: number
    }
    isLoading: boolean
  }> = useMaybeSuspenseQueries({
    queries: chartSerieSettings
      .filter((settings) => settings?.filters?.length > 0)
      .map((chartSerie) => {
        const dataIds: string[] = []
        const chainIds: string[] = []
        const marketSectorIds: string[] = []
        const productIds: string[] = []
        const assetIds: string[] = []
        const referenceAssetIds: string[] = []
        const venues: Array<{ data_id: string; product_id: string }> = []

        chartSerie.filters.forEach((filter) => {
          if (filter.type === "project") {
            dataIds.push(...filter.values)
          }
          if (filter.type === "blockchain") {
            chainIds.push(...filter.values)
          }
          if (filter.type === "market_sector") {
            marketSectorIds.push(...filter.values)
          }
          if (filter.type === "product") {
            productIds.push(...filter.values)
          }
          if (filter.type === "asset") {
            assetIds.push(...filter.values)
          }
          if (filter.type === "reference_asset") {
            referenceAssetIds.push(...filter.values)
          }
          if (filter.type === "venue") {
            // Venue values are encoded as "data_id:product_id" (Filter.values
            // is string[]); skip any malformed value that lacks a product.
            filter.values.forEach((value) => {
              const { dataId, productId } = parseVenue(value)
              if (!productId) return
              venues.push({ data_id: dataId, product_id: productId })
            })
          }
        })

        const productIdsFilter = productIds.length > 0 ? productIds : undefined
        const assetIdsFilter = assetIds.length > 0 ? assetIds : undefined
        const referenceAssetIdsFilter =
          referenceAssetIds.length > 0 ? referenceAssetIds : undefined
        const venuesFilter = venues.length > 0 ? venues : undefined
        const limit =
          chartSerie.display === "percentage" ||
          chartSerie.ratioMetric ||
          chartSerie.groupByRemainingInOther
            ? undefined
            : (chartSerie.limit ?? undefined)

        // Cumulative series keep the exact `zoom`-point window: they sum
        // over it, so the extra baseline day would inflate the running
        // total by one day's value.
        const includeIntervalStart =
          chartHasDailyPercentChangeSerie && chartSerie.display !== "cumulative"

        return {
          queryKey: [
            "getTimeseries",
            chartSerie.metric,
            chartSerie.ratioMetric,
            dataIds,
            chainIds,
            marketSectorIds,
            assetIds,
            referenceAssetIds,
            zoom,
            chartSerie.groupBy,
            productIdsFilter,
            venuesFilter,
            start,
            end,
            chartSerie.limit,
            chartSerie.groupByRemainingInOther,
            includeProductsInProjectBreakdown,
            chartSerie.bridged,
            includeIntervalStart,
          ],
          queryFn: async () => {
            const metricId = chartSerie.metric.replace(/-/g, "_")
            const ratioMetricId = chartSerie.ratioMetric?.replace(/-/g, "_")

            const promises = []

            const includeSelf = metricIncludesSelf(metricId)

            const groupBy = getGroupByName(
              chartSerie.groupBy as
                | "chain"
                | "project+chain"
                | "market-sector"
                | "reference-asset"
                | "project"
                | "product"
                | "product+chain"
                | "project-product"
                | "product-market-sector"
                | null
            )

            const includeProductsInProjectBreakdownFlag =
              includeProductsInProjectBreakdown &&
              groupBy === "projects" &&
              !marketSectorIds?.includes("blockchains-l3")

            promises.push(
              trpcClient.metrics.getTimeseries.query(
                {
                  data_ids: dataIds.length > 0 ? dataIds : undefined,
                  chain_ids: chainIds.length > 0 ? chainIds : undefined,
                  market_sector_ids:
                    marketSectorIds.length > 0
                      ? marketSectorIds.map((id) => id.replace(/_/g, "-"))
                      : undefined,
                  product_ids: productIdsFilter,
                  asset_ids: assetIdsFilter,
                  reference_asset_ids: referenceAssetIdsFilter,
                  venues: venuesFilter,
                  metric_ids: [metricId],
                  interval: zoom,
                  include_interval_start: includeIntervalStart || undefined,
                  groupBy,
                  start,
                  end,
                  limit: limit ?? undefined,
                  includeSelf,
                  include_products_in_project_breakdown:
                    includeProductsInProjectBreakdownFlag,
                  bridged: chartSerie.bridged,
                },
                {
                  context: {
                    skipBatch: true,
                  },
                }
              )
            )

            if (ratioMetricId) {
              promises.push(
                trpcClient.metrics.getTimeseries.query(
                  {
                    data_ids: dataIds.length > 0 ? dataIds : undefined,
                    chain_ids: chainIds.length > 0 ? chainIds : undefined,
                    market_sector_ids:
                      marketSectorIds.length > 0
                        ? marketSectorIds.map((id) => id.replace(/_/g, "-"))
                        : undefined,
                    product_ids: productIdsFilter,
                    asset_ids: assetIdsFilter,
                    reference_asset_ids: referenceAssetIdsFilter,
                    venues: venuesFilter,
                    metric_ids: [ratioMetricId],
                    interval: zoom,
                    include_interval_start: includeIntervalStart || undefined,
                    groupBy,
                    start,
                    end,
                    limit: limit ?? undefined,
                    include_products_in_project_breakdown:
                      includeProductsInProjectBreakdownFlag,
                    bridged: chartSerie.bridged,
                  },
                  {
                    context: {
                      skipBatch: true,
                    },
                  }
                )
              )
            }

            const result = await Promise.all(promises)

            if (ratioMetricId) {
              // Combine the two results and calculate ratios
              const metricData = result[0]
              const ratioMetricData = result[1]

              if (!metricData?.data || !ratioMetricData?.data) {
                return metricData
              }

              // Create a map for quick lookup of ratio metric data
              const ratioDataMap = new Map<string, number>()

              ratioMetricData.data.forEach((item) => {
                const key = `${item.data_id || ""}${
                  item.chain_id ? `-${item.chain_id}` : ""
                }${item.market_sector ? `-${item.market_sector}` : ""}${
                  item.product_id ? `-${item.product_id}` : ""
                }-${item.timestamp}`

                ratioDataMap.set(key, item.value ?? 0)
              })

              // Merge the data by calculating ratios
              const mergedData = metricData.data.map((item) => {
                const key = `${item.data_id || ""}${
                  item.chain_id ? `-${item.chain_id}` : ""
                }${item.market_sector ? `-${item.market_sector}` : ""}${
                  item.product_id ? `-${item.product_id}` : ""
                }-${item.timestamp}`

                const metricValue = item.value ?? 0

                const ratioValue = ratioDataMap.get(key) ?? 0

                // Avoid division by zero
                const calculatedValue =
                  ratioValue !== 0 ? metricValue / ratioValue : 0

                return {
                  ...item,
                  value: calculatedValue,
                }
              })

              return {
                ...metricData,
                data: mergedData,
              }
            }

            if (chartSerie.groupByRemainingInOther && chartSerie.limit) {
              return aggregateBeyondLimitDynamic(result[0] as Payload, {
                limit: chartSerie.limit,
                otherLabel: "other",
              })
            }

            return result[0]
          },
        }
      }),
  })

  const isLoading = useMemo(
    () =>
      isProjectsLoading ||
      isProjectMarketSectorsLoading ||
      isProductMarketSectorsLoading ||
      isAssetsLoading ||
      isChainsLoading ||
      isProductsLoading ||
      isMetricsConfigurationLoading ||
      results.some((result) => result.isLoading),
    [
      isProjectsLoading,
      isProjectMarketSectorsLoading,
      isProductMarketSectorsLoading,
      isAssetsLoading,
      isChainsLoading,
      isProductsLoading,
      isMetricsConfigurationLoading,
      results,
    ]
  )

  // chartSerieSettings keeps the same reference when the titles change so we
  // need some other way to trigger the useMemo below to recompute the series
  // and seriesData when the titles change.
  const _chartSeriesSettingsTitles = JSON.stringify(
    chartSerieSettings.map((setting) => setting.title)
  )

  const { series, seriesData } = useMemo(() => {
    const series: Array<ChartSerie> = []
    const seriesData: Record<string, ChartSerieTimeData> = {}

    if (isLoading) {
      return {
        series,
        seriesData,
      }
    }

    const metricsConfigurationDictionary = toDictionary(
      metricsConfiguration ?? [],
      "slug"
    )
    // Every serie metric comes from the metrics configuration list
    const getMetricConfig = (metric: string) => {
      const metricConfig = metricsConfigurationDictionary.get(metric)
      if (!metricConfig) {
        throw new Error(`No configuration for metric "${metric}"`)
      }
      return metricConfig
    }

    const projectsDictionary = toDictionary(projects ?? [], "data_id")
    const assetsDictionary = toDictionary(assets ?? [], "asset_id")
    const chainsDictionary = toDictionary(chains ?? [], "chain_id")
    const marketSectorsDictionary = toDictionary(marketSectors ?? [], "id")
    // Keyed by data_id+product_id: product_id alone collides across projects
    // that reuse the same product_id (e.g. `perps` on both hyperliquid and tradexyz).
    const productsDictionary = new Map(
      (products ?? []).map((product) => [
        `${product.data_id}:${product.product_id}`,
        product,
      ])
    )
    const yAxisMap = getYaxisMergePaths(
      chartSerieSettings,
      metricsConfigurationDictionary
    )

    const fetchableChartSerieSettings = chartSerieSettings.filter(
      (settings) => settings.filters.length > 0
    )

    // When the chart has a single metric (across all serie configs), the
    // metric name in the per-series label is redundant and just clutters the
    // tooltip — e.g. "stETH - Asset APY" on a chart that's already titled
    // "Top liquid staking tokens by APY".
    const isSingleMetricChart =
      new Set(fetchableChartSerieSettings.map((s) => s.metric)).size <= 1

    let tempSeries: Array<ChartSerie> = []

    for (const [index, chartSetting] of fetchableChartSerieSettings.entries()) {
      const metricConfig = getMetricConfig(chartSetting.metric)

      const serieType = chartSetting.chart_type
      const highchartSerieType = convertChartTypeToHighcharts(serieType)
      const isAggregated = !chartSetting.groupBy
      const isCumulative = chartSetting.display === "cumulative"
      const isPercentage = chartSetting.display === "percentage"

      let chartInfo = isAggregated
        ? [
            {
              data_id: "aggregated",
              name: "Aggregated",
              venue_data_id: undefined as string | undefined,
              venue_product_id: undefined as string | undefined,
            },
          ]
        : (results[index]?.data?.sorted_data_ids ?? [])

      const groupByProjectOrProjectAndChain =
        chartSetting.groupBy === "project" ||
        chartSetting.groupBy === "project+chain"

      const hasOnlyBlockchainFilter =
        chartSetting.filters.some((filter) => filter.type === "blockchain") &&
        !chartSetting.filters.some((filter) => filter.type === "project") &&
        !chartSetting.filters.some((filter) => filter.type === "market_sector")

      if (hasOnlyBlockchainFilter && groupByProjectOrProjectAndChain) {
        // TODO: We should be using this data for the chart instead of just the sort order
        chartInfo = chartInfo.filter(
          (item) =>
            !chartSetting.filters
              .find((filter) => filter.type === "blockchain")
              ?.values.includes(item.data_id ?? "")
        )
      }

      const metricId = chartSetting.metric.replace(/-/g, "_")
      const ratioMetricId = chartSetting.ratioMetric?.replace(/-/g, "_")

      // For product-market-sector, deduplicate chartInfo by market_sector_id
      // since we want one series per market sector (aggregating all products)
      if (chartSetting.groupBy === "product-market-sector") {
        const seenMarketSectors = new Set<string>()
        chartInfo = chartInfo.filter((info) => {
          if (
            "market_sector_id" in info &&
            info.market_sector_id &&
            !seenMarketSectors.has(info.market_sector_id)
          ) {
            seenMarketSectors.add(info.market_sector_id)
            return true
          }
          return false
        })
      }

      chartInfo
        .filter((info) => {
          if (!includeAllChains && "chain_id" in info && info.chain_id) {
            const chain = chainsDictionary.get(info.chain_id)

            if (!chain) {
              return false
            }
          }

          return true
        })
        .map((info) => {
          // For `venue` group-by, the relevant project is the venue's own
          // (e.g. "Aave"), not the asset's issuer -- info.data_id is unset
          // for that grouping, so fall back to venue_data_id.
          const project = projectsDictionary.get(
            info.data_id ?? info.venue_data_id ?? ""
          )

          return {
            ...info,
            name: project?.name,
            legendLabel: formatLabel({
              groupBy: chartSetting.groupBy,
              info,
              project,
              projects: projectsDictionary,
              chains: chainsDictionary,
              marketSectors: marketSectorsDictionary,
              products: productsDictionary,
              assets: assetsDictionary,
              productAssets: productToIssuerDictionary,
              showProductSymbol,
              includeProductsInProjectBreakdown,
              hideProductProjectSuffix,
            }),
          }
        })
        .map((info, index) => {
          const innerKey = getInnerKey(
            chartSetting.groupBy,
            chartSetting.id,
            info
          )

          const format = chartSetting.ratioMetric
            ? (chartSetting.metricFormatOverride ?? "ratio")
            : (metricConfig.format as SERIE_FORMAT)

          const serie: ChartSerie = {
            index: index,
            metricId: metricId,
            ratioMetricId: ratioMetricId,
            yAxis: yAxisMap.get(chartSetting.id) ?? chartSetting.id,
            id: chartSetting.id,
            name: innerKey,
            label: isAggregated
              ? chartSetting.title
              : isSingleMetricChart && !chartSetting.title
                ? info.legendLabel
                : `${info.legendLabel} - ${chartSetting.title || metricConfig.title}`,
            seriesTitle: chartSetting.title || metricConfig.title,
            type: highchartSerieType,
            format,
            groupingApproximation: getMetricApproximation(metricConfig),
            isStatic: metricConfig.static,
            cumulative: isCumulative,
            stack: getStack(serieType, isPercentage),
            percentage: isPercentage,
            visible:
              chartSetting.visible &&
              Array.isArray(chartSetting.visible) &&
              chartSetting.visible.length > 0
                ? chartSetting.visible.includes(innerKey)
                : true,
            info,
            groupByRemainingInOther: chartSetting.groupByRemainingInOther,
          }

          tempSeries.push(serie)
        })

      fetchableChartSerieSettings.forEach((settings, index) => {
        const result = results[index]
        const metricConfig = getMetricConfig(settings.metric)
        const key = settings.id
        const sortedDataIds = !settings.groupBy
          ? [{ data_id: "aggregated" }]
          : (result?.data?.sorted_data_ids ?? [])

        // Build a shared per-period denominator from every distinct daily
        // timestamp in this chart's result. Without this, each series averages
        // over its own active days — so a chain that launched mid-month gets a
        // small denominator and inflates the breakdown total. Sharing the
        // denominator across series makes all groupings reconcile to the
        // project-level total. Only relevant when granularity > day.
        const periodDenominators =
          granularity !== "day"
            ? computePeriodDenominators(
                ((result?.data?.data as TimeseriesItem[]) ?? []).map((item) =>
                  new Date(item.timestamp).getTime()
                ),
                calculateGranularityUnit(granularity)
              )
            : undefined

        ;(sortedDataIds as SortedDataIds).forEach((data) => {
          const innerKey = getInnerKey(settings.groupBy, key, data)
          let innerData: Array<TimeseriesItem> = []

          const resultData = result?.data?.data as TimeseriesItem[]

          switch (settings.groupBy) {
            case "project+chain":
            case "chain-project":
              innerData = resultData.filter(
                (item) =>
                  item.data_id === data.data_id &&
                  item.chain_id === data.chain_id
              )
              break
            case "chain":
              if (data.market_sector_id) {
                innerData = resultData.filter(
                  (item) => item.market_sector === data.market_sector_id
                )
              } else if (data.data_id && data.chain_id) {
                innerData = resultData.filter(
                  (item) =>
                    item.data_id === data.data_id &&
                    item.chain_id === data.chain_id
                )
              } else if (data.chain_id) {
                innerData = resultData.reduce(
                  (acc, item) => {
                    if (item.chain_id === data.chain_id) {
                      const existing = acc.find(
                        (x) => x.timestamp === item.timestamp
                      )
                      if (existing) {
                        existing.value =
                          (existing.value ?? 0) + (item.value ?? 0)
                      } else {
                        acc.push({
                          ...item,
                          value: item.value ?? 0,
                          data_id: item.data_id ?? "",
                        })
                      }
                    }
                    return acc
                  },
                  [] as Array<TimeseriesItem>
                )
              } else {
                innerData = resultData.filter(
                  (item) => item.data_id === data.data_id
                )
              }
              break
            case "market-sector":
              innerData = resultData.filter(
                (item) =>
                  item.market_sector === data.market_sector_id ||
                  item.market_sector_id === data.market_sector_id
              )
              break
            case "reference-asset":
              innerData = resultData.filter(
                (item) => item.reference_asset === data.reference_asset
              )
              break
            case "project":
              innerData = resultData.filter(
                (item) => item.data_id === data.data_id
              )
              break
            case "product":
              innerData = resultData.filter(
                (item) => item.product_id === data.product_id
              )
              break
            case "product+chain":
              innerData = resultData.filter(
                (item) =>
                  item.product_id === data.product_id &&
                  item.chain_id === data.chain_id
              )
              break
            case "chain-market-sector":
              innerData = resultData.filter(
                (item) =>
                  item.chain_id === data.chain_id &&
                  item.market_sector_id === data.market_sector_id
              )
              break
            case "product-market-sector":
              // Aggregate all products in the same market sector
              innerData = resultData.filter(
                (item) => item.market_sector_id === data.market_sector_id
              )
              break
            case "assets":
              innerData = resultData.filter(
                (item) => item.asset_id === data.asset_id
              )
              break
            case "chain-asset":
              innerData = resultData.filter(
                (item) =>
                  item.chain_id === data.chain_id &&
                  item.asset_id === data.asset_id
              )
              break
            case "venue":
              innerData = resultData.filter(
                (item) =>
                  item.venue_data_id === data.venue_data_id &&
                  item.venue_product_id === data.venue_product_id
              )
              break
            case "asset+venue":
              innerData = resultData.filter(
                (item) =>
                  item.asset_id === data.asset_id &&
                  item.venue_data_id === data.venue_data_id &&
                  item.venue_product_id === data.venue_product_id
              )
              break
            case "venue+chain":
              innerData = resultData.filter(
                (item) =>
                  item.venue_data_id === data.venue_data_id &&
                  item.venue_product_id === data.venue_product_id &&
                  item.chain_id === data.chain_id
              )
              break
            case "market-sector+venue":
              innerData = resultData.filter(
                (item) =>
                  (item.market_sector_id === data.market_sector_id ||
                    item.market_sector === data.market_sector_id) &&
                  item.venue_data_id === data.venue_data_id &&
                  item.venue_product_id === data.venue_product_id
              )
              break
            default:
              innerData = resultData
              break
          }

          // Merge values sharing a timestamp via a Map — a findIndex-based
          // merge is O(n²) over the raw (pre-grouping) rows and dominated the
          // chart render time for multi-year daily series.
          const valueByTimestamp = new Map<number, number | null>()
          for (const item of innerData) {
            const timestamp = new Date(item.timestamp).getTime()
            if (valueByTimestamp.has(timestamp)) {
              valueByTimestamp.set(
                timestamp,
                (valueByTimestamp.get(timestamp) ?? 0) + (item.value ?? 0)
              )
            } else {
              valueByTimestamp.set(timestamp, item.value)
            }
          }
          let processedData: ChartSerieTimeData = [
            ...valueByTimestamp.entries(),
          ].sort((a, b) => a[0] - b[0])

          // Apply granularity grouping if needed
          if (granularity !== "day") {
            const data = resultData.map((item) => [
              new Date(item.timestamp).getTime(),
              item.value,
            ]) as unknown as ChartSerieTimeData

            // Detect partial periods before grouping (for efficiency)
            const partialPeriods = detectPartialPeriods(data, granularity)

            processedData = groupDataBasedOnGranularity(
              processedData,
              {
                approximation: getMetricApproximation(metricConfig),
                unit: calculateGranularityUnit(granularity),
              },
              convertApiToZoom(zoom),
              periodDenominators
            )

            // Apply partial period filtering if needed
            if (!showCurrentPeriod) {
              processedData = filterPartialPeriods(
                processedData,
                partialPeriods,
                zoom,
                convertApiToZoom(zoom),
                granularity
              )
            }
          }

          seriesData[innerKey] = processedData
        })
      })

      // Filter out any series that doesn't have a legend label
      // Mainly for product group by
      tempSeries = tempSeries.filter(
        (serie) => serie.info?.legendLabel !== undefined
      )

      // Stacked bars of static metrics read as "one size per group over the
      // range" (weekly/monthly buckets render period averages), but the API
      // ranks static series by their latest value — so bar segments could
      // stack out of size order (DEV-5152). Rank the DRAWING order by total
      // drawn mass, expressed via the Highcharts `index` stamped in the
      // final push below; the returned array stays in the API's latest-value
      // order for legend consumers. Line/area series are read by where they
      // end and flow metrics already arrive range-ranked, so both keep the
      // API order. "Other" stays pinned last in the stack.
      let stackedDrawOrder: Map<string, number> | null = null
      if (
        !ratioMetricId &&
        convertChartTypeToHighcharts(chartSetting.chart_type) === "column" &&
        metricConfig.static &&
        granularity !== "day"
      ) {
        // Apply the top-N cut while still in the API's latest-value order —
        // selection stays "biggest now" even on paths where the API returns
        // every entity (e.g. percentage display) and the shared slice below
        // would otherwise run after the stacking sort and cut by mass.
        tempSeries = tempSeries.slice(
          0,
          chartSetting.limit && !chartSetting.groupByRemainingInOther
            ? chartSetting.limit
            : Infinity
        )

        const rangeTotals = new Map<string, number>()
        for (const serie of tempSeries) {
          const data = seriesData[serie.name] ?? []
          rangeTotals.set(
            serie.name,
            data.reduce((sum, [, value]) => sum + (value ?? 0), 0)
          )
        }

        // Stamp colors in the array's latest-value order, so color 0 goes
        // to the entity that's biggest now — matching the latest-sorted
        // sidebar legend. Also keeps explicit Studio color configs (indexed
        // arrays) keyed to the latest ranking.
        const baseGroupIndex = getCurrentGroupCount(series)
        tempSeries.forEach((serie, serieIndex) => {
          serie.color = getSeriesColor({
            colorsConfig: chartSetting.colors,
            groupId: serie.info?.legendLabel === "Other" ? "Other" : serie.name,
            groupIndex: serieIndex,
            globalGroupIndex: baseGroupIndex + serieIndex,
            resolvePaletteColors: false,
          })
        })

        // Rank by mass without reordering tempSeries — the ranking becomes
        // each serie's Highcharts `index` (draw position) in the push below.
        const massSorted = [...tempSeries].sort((a, b) => {
          const aIsOther = a.info?.legendLabel === "Other"
          const bIsOther = b.info?.legendLabel === "Other"
          if (aIsOther !== bIsOther) return aIsOther ? 1 : -1

          const diff =
            (rangeTotals.get(b.name) ?? 0) - (rangeTotals.get(a.name) ?? 0)
          return metricConfig.is_lower_value_better ? -diff : diff
        })
        stackedDrawOrder = new Map(
          massSorted.map((serie, drawIndex) => [serie.name, drawIndex])
        )
      }

      if (ratioMetricId) {
        // Resort series based on highest values for ratio metrics
        const seriesInChart = tempSeries.filter(
          (serie) => serie.id === chartSetting.id
        )

        const data = seriesInChart
          .map((serie) => {
            const seriesDataInChart = seriesData[serie.name]
            return {
              name: serie.name,
              value: calculateGranularity({
                serie,
                data: seriesDataInChart ?? [],
                granularity,
                zoom,
                showCurrentPeriod,
              }),
            }
          })
          .filter((serie) => serie.value !== null)
          .sort((a, b) => (b?.value ?? 0) - (a?.value ?? 0))

        tempSeries = tempSeries
          .sort((a, b) => {
            const aIndex = data.findIndex((d) => d?.name === a.name)
            const bIndex = data.findIndex((d) => d?.name === b.name)

            if (aIndex === -1 && bIndex === -1) return 0
            if (aIndex === -1) return 1
            if (bIndex === -1) return -1

            return aIndex - bIndex
          })
          .map((serie, serieIndex) => {
            const globalGroupIndex = getCurrentGroupCount(series) + serieIndex

            serie.color = getSeriesColor({
              colorsConfig: chartSetting.colors,
              groupId:
                serie.info?.legendLabel === "Other" ? "Other" : serie.name,
              groupIndex: serieIndex,
              globalGroupIndex,
              resolvePaletteColors: false,
            })

            return serie
          })
      }

      // // we want to resort based on percentage
      if (
        chartSetting.display === "percentage" &&
        !["bar-stacked", "area-stacked"].includes(chartSetting.chart_type)
      ) {
        const seriesInChart = tempSeries.filter(
          (serie) => serie.id === chartSetting.id
        )

        const data = seriesInChart
          .map((serie) => {
            const seriesDataInChart = seriesData[serie.name]

            return {
              name: serie.name,
              percentage: calculateGranularity({
                serie,
                data: seriesDataInChart ?? [],
                granularity,
                zoom,
                showCurrentPeriod,
              }),
            }
          })
          .filter((serie) => serie.percentage !== null)
          .sort((a, b) => {
            if (!a || !b) return 0
            if (metricConfig.is_lower_value_better) {
              return (a?.percentage ?? 0) - (b?.percentage ?? 0)
            }
            return (b?.percentage ?? 0) - (a?.percentage ?? 0)
          })

        const currentGroupCount = getCurrentGroupCount(series)
        // Highcharts `index` must be the serie's final position in the array:
        // with equal per-group indexes Highcharts falls back to insertion
        // order and in-place updates never move a series, so bars could
        // freeze in a stale order while the legend re-renders in the new one.
        const drawPositionBase = series.length

        series.push(
          ...tempSeries
            .sort((a, b) => {
              const aIndex = data.findIndex((d) => d?.name === a.name)
              const bIndex = data.findIndex((d) => d?.name === b.name)

              if (aIndex === -1 && bIndex === -1) return 0
              if (aIndex === -1) return 1
              if (bIndex === -1) return -1

              return aIndex - bIndex
            })
            .map((serie, serieIndex) => {
              const globalGroupIndex = currentGroupCount + serieIndex

              serie.color = getSeriesColor({
                colorsConfig: chartSetting.colors,
                groupId:
                  serie.info?.legendLabel === "Other" ? "Other" : serie.name,
                groupIndex: serieIndex,
                globalGroupIndex,
                resolvePaletteColors: false,
              })

              return {
                ...serie,
                index: drawPositionBase + serieIndex,
              }
            })
            .slice(
              0,
              chartSetting.limit && !chartSetting.groupByRemainingInOther
                ? chartSetting.limit
                : Infinity
            )
        )
      } else {
        const currentGroupCount = getCurrentGroupCount(series)
        // See the percentage branch above: index = final array position so
        // Highcharts honors order changes on update.
        const drawPositionBase = series.length

        series.push(
          ...tempSeries
            .map((serie, serieIndex) => {
              const globalGroupIndex = currentGroupCount + serieIndex

              // Series pre-colored above (static bar stacking) keep their
              // latest-ranked palette position — don't rekey colors to the
              // draw order.
              serie.color =
                serie.color ??
                getSeriesColor({
                  colorsConfig: chartSetting.colors,
                  groupId:
                    serie.info?.legendLabel === "Other" ? "Other" : serie.name,
                  groupIndex: serieIndex,
                  globalGroupIndex,
                  resolvePaletteColors: false,
                })

              return {
                ...serie,
                // Static-bar stacking: draw position comes from the mass
                // ranking, decoupled from the array position. Ranks are a
                // complete permutation of the group, so indexes stay unique.
                index:
                  drawPositionBase +
                  (stackedDrawOrder?.get(serie.name) ?? serieIndex),
              }
            })
            .slice(
              0,
              chartSetting.limit && !chartSetting.groupByRemainingInOther
                ? chartSetting.limit
                : Infinity
            )
        )
      }

      tempSeries = []
    }

    return {
      series,
      seriesData,
    }
  }, [
    chartSerieSettings,
    projects,
    metricsConfiguration,
    chains,
    marketSectors,
    showCurrentPeriod,
    isLoading,
    assets,
    granularity,
    includeAllChains,
    includeProductsInProjectBreakdown,
    productToIssuerDictionary,
    products,
    results,
    showProductSymbol,
    hideProductProjectSuffix,
    zoom,
  ])

  return {
    series,
    seriesData,
    totalLegendItems: results?.[0]?.data?.total_sorted_data_ids,
    isLoading,
  }
}

const calculateGranularity = ({
  serie,
  data,
  granularity,
  zoom,
  showCurrentPeriod,
}: {
  serie: ChartSerie
  data: ChartSerieTimeData
  granularity: GranularityType
  zoom: Interval
  showCurrentPeriod?: boolean
}) => {
  if (granularity !== "day") {
    // Detect partial periods before grouping
    const partialPeriods = detectPartialPeriods(data, granularity)

    data = groupDataBasedOnGranularity(
      data as ChartSerieTimeData,
      {
        approximation: serie.groupingApproximation,
        unit: calculateGranularityUnit(granularity),
      },
      convertApiToZoom(zoom)
    )

    if (!showCurrentPeriod) {
      data = filterPartialPeriods(
        data,
        partialPeriods,
        zoom,
        convertApiToZoom(zoom),
        granularity
      )
    }
  }

  if (!serie.ratioMetricId) {
    data = getPercentChangeSeries({
      serie,
      serieData: data,
      granularity,
    })
  }

  return data[data.length - 1]?.[1]
}
